<template>
	<div class="closeout-products">
		<!-- Header with cart dropdown -->
		<div class="closeout-header d-flex justify-content-between align-items-start mb-4">
			<div></div>
			<CloseoutCartDropdown
				:cart-count="cartCount"
				:cart-total="cartTotal"
				:cart-url="cartUrl"
				:checkout-url="checkoutUrl"
				:items="cartItems"
				:loading="cartLoading"
				@open="fetchCart"
				@remove="removeFromCart"
			/>
		</div>

		<!-- Search Filters -->
		<CloseoutFilters
			:filters="filters"
			:categories="categories"
			:manufacturers="manufacturers"
			@update:filters="Object.assign(filters, $event)"
			@apply="applyFilters"
			@clear="clearFilters"
		/>

		<!-- Loading -->
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<!-- Error -->
		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<!-- Results -->
		<template v-else>
			<!-- Pagination top -->
			<div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
				<p class="mb-0 fw-italic">
					<em>
						Showing {{ rangeStart }}-{{ rangeEnd }} of {{ totalProducts }}
						Items
					</em>
				</p>
				<div class="d-flex align-items-center gap-2">
					<span>Page {{ currentPage }} of {{ totalPages }}</span>
					<CloseoutPagination
						:current-page="currentPage"
						:total-pages="totalPages"
						@page="goToPage"
					/>
				</div>
			</div>

			<!-- Products Table -->
			<CloseoutTable :products="products" @view="openModal" @add-to-cart="addToCart" />

			<!-- Pagination bottom -->
			<div class="d-flex justify-content-end mt-3">
				<CloseoutPagination
					:current-page="currentPage"
					:total-pages="totalPages"
					@page="goToPage"
				/>
			</div>
		</template>

		<!-- Product Detail Modal -->
		<CloseoutModal :product="modalProduct" @close="closeModal" @add-to-cart="addToCart" />
	</div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import CloseoutFilters from './closeout/CloseoutFilters.vue';
import CloseoutPagination from './closeout/CloseoutPagination.vue';
import CloseoutTable from './closeout/CloseoutTable.vue';
import CloseoutModal from './closeout/CloseoutModal.vue';
import CloseoutCartDropdown from './closeout/CloseoutCartDropdown.vue';

export default {
	name: 'CloseoutProducts',
	components: {
		CloseoutFilters,
		CloseoutPagination,
		CloseoutTable,
		CloseoutModal,
		CloseoutCartDropdown,
	},
	setup() {
		const ajaxUrl = window.closeoutData?.ajaxUrl || window.jaeckleData?.ajaxUrl;
		const nonce = window.closeoutData?.nonce || window.jaeckleData?.nonce;
		const cartUrl = window.closeoutData?.cartUrl || '/cart';
		const checkoutUrl = window.closeoutData?.checkoutUrl || '/checkout';

		const products = ref([]);
		const categories = ref([]);
		const manufacturers = ref([]);
		const loading = ref(true);
		const error = ref(null);
		const cartCount = ref(0);
		const cartTotal = ref('$0.00');
		const cartItems = ref([]);
		const cartLoading = ref(false);
		const totalProducts = ref(0);
		const totalPages = ref(0);
		const currentPage = ref(1);
		const perPage = ref(50);
		const modalProduct = ref(null);

		const filters = reactive({
			search: '',
			category: '',
			manufacturer: '',
			minQty: 1,
		});

		const rangeStart = computed(() =>
			totalProducts.value > 0 ? (currentPage.value - 1) * perPage.value + 1 : 0
		);
		const rangeEnd = computed(() =>
			Math.min(currentPage.value * perPage.value, totalProducts.value)
		);

		// --- Data fetching ---

		async function fetchProducts(page = 1) {
			loading.value = true;
			error.value = null;

			const formData = new FormData();
			formData.append('action', 'search_closeout_products');
			formData.append('nonce', nonce);
			formData.append('page', page);
			formData.append('per_page', perPage.value);
			if (filters.search) formData.append('search', filters.search);
			if (filters.category) formData.append('category', filters.category);
			if (filters.manufacturer) formData.append('manufacturer', filters.manufacturer);
			if (filters.minQty) formData.append('min_qty', filters.minQty);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();

				if (json.success) {
					products.value = json.data.products.map(p => ({
						...p,
						_qty: 0,
						_adding: false,
						_added: false,
					}));
					totalProducts.value = json.data.total;
					totalPages.value = json.data.pages;
					currentPage.value = json.data.current_page;
				} else {
					error.value = 'Failed to load products.';
				}
			} catch (e) {
				error.value = 'Network error. Please try again.';
			} finally {
				loading.value = false;
			}
		}

		async function fetchCategories() {
			const formData = new FormData();
			formData.append('action', 'get_closeout_categories');
			formData.append('nonce', nonce);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();
				if (json.success) {
					categories.value = json.data;
				}
			} catch {
				// Silently fail
			}
		}

		async function fetchManufacturers() {
			const formData = new FormData();
			formData.append('action', 'get_closeout_manufacturers');
			formData.append('nonce', nonce);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();
				if (json.success) {
					manufacturers.value = json.data;
				}
			} catch {
				// Silently fail
			}
		}

		async function fetchCart() {
			cartLoading.value = true;
			const formData = new FormData();
			formData.append('action', 'get_closeout_cart');
			formData.append('nonce', nonce);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();
				if (json.success) {
					cartCount.value = json.data.cart_count;
					cartTotal.value = json.data.cart_total;
					cartItems.value = json.data.items.map(item => ({
						...item,
						_removing: false,
					}));
				}
			} catch {
				// Silently fail
			} finally {
				cartLoading.value = false;
			}
		}

		// --- Actions ---

		async function addToCart(product) {
			if (!product._qty || product._qty < 1) return;

			product._adding = true;
			const formData = new FormData();
			formData.append('action', 'closeout_add_to_cart');
			formData.append('nonce', nonce);
			formData.append('product_id', product.id);
			formData.append('quantity', product._qty);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();
				if (json.success) {
					product._added = true;
					cartCount.value = json.data.cart_count;
					cartTotal.value = json.data.cart_total;
					setTimeout(() => {
						product._added = false;
					}, 3000);
				} else {
					alert(json.data?.message || 'Could not add to order.');
				}
			} catch {
				alert('Network error. Please try again.');
			} finally {
				product._adding = false;
			}
		}

		async function removeFromCart(item) {
			item._removing = true;
			const formData = new FormData();
			formData.append('action', 'closeout_remove_from_cart');
			formData.append('nonce', nonce);
			formData.append('cart_item_key', item.cart_item_key);

			try {
				const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
				const json = await res.json();
				if (json.success) {
					cartCount.value = json.data.cart_count;
					cartTotal.value = json.data.cart_total;
					cartItems.value = cartItems.value.filter(
						i => i.cart_item_key !== item.cart_item_key
					);
				}
			} catch {
				// Silently fail
			} finally {
				item._removing = false;
			}
		}

		function applyFilters() {
			currentPage.value = 1;
			fetchProducts(1);
		}

		function clearFilters() {
			filters.search = '';
			filters.category = '';
			filters.manufacturer = '';
			filters.minQty = 1;
			currentPage.value = 1;
			fetchProducts(1);
		}

		function goToPage(page) {
			if (page < 1 || page > totalPages.value) return;
			fetchProducts(page);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}

		function openModal(product) {
			modalProduct.value = product;
			document.body.style.overflow = 'hidden';
		}

		function closeModal() {
			modalProduct.value = null;
			document.body.style.overflow = '';
		}

		function onKeydown(e) {
			if (e.key === 'Escape' && modalProduct.value) {
				closeModal();
			}
		}

		// --- Lifecycle ---

		onMounted(() => {
			fetchCategories();
			fetchManufacturers();
			fetchProducts(1);
			fetchCart();
			document.addEventListener('keydown', onKeydown);
		});

		onUnmounted(() => {
			document.removeEventListener('keydown', onKeydown);
			document.body.style.overflow = '';
		});

		return {
			products,
			categories,
			manufacturers,
			loading,
			error,
			filters,
			cartCount,
			cartTotal,
			cartItems,
			cartLoading,
			cartUrl,
			checkoutUrl,
			totalProducts,
			totalPages,
			currentPage,
			rangeStart,
			rangeEnd,
			modalProduct,
			applyFilters,
			clearFilters,
			goToPage,
			addToCart,
			removeFromCart,
			fetchCart,
			openModal,
			closeModal,
		};
	},
};
</script>
